import { safeLocalStorage } from '@/lib/browserStorage';
// Export helpers for drill runs — CSV (string-built) and PDF (jsPDF table)
import { pdfText } from "@/lib/pdfText";
import { deliverExport } from "@/components/drill/nativeExport";
import { getTotalDepth } from "@/lib/depthCalculations";
export async function exportRunsToCSV(runs, filename = "drill_runs.csv", holeType = null) {
  if (!runs || runs.length === 0) {
    alert("No drill runs to export.");
    return;
  }

  const columns = [
    { key: "record_type", label: "Type", display: (r) => (r.record_type === "chip_section" ? "Chip Section" : "Run") },
    { key: "hole_id", label: "Hole ID" },
    { key: "run_number", label: "Run #" },
    { key: "date", label: "Date" },
    { key: "start_time", label: "Start Time" },
    { key: "end_time", label: "End Time" },
    { key: "viscosity_check", label: "Viscosity (s)" },
    { key: "flow_rate", label: "Flow Rate (L/min)" },
    { key: "from_depth", label: "From (m)" },
    { key: "to_depth", label: "To (m)" },
    { key: "recovered", label: "Recovered (m)" },
    { key: "gain_loss", label: "Gain/Loss (m)" },
    { key: "driller", label: "Driller" },
    { key: "lithology", label: "Lithology" },
    { key: "remarks", label: "Remarks" },
    { key: "azimuth", label: "Azimuth" },
  ];

  const exportColumns = holeType === "Conventional"
    ? columns.filter((c) => c.key !== "viscosity_check")
    : columns;

  const escape = (val) => {
    if (val == null) return "";
    const s = String(val).replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  };

  const header = exportColumns.map((c) => c.label).join(",");
  const rows = runs.map((r) =>
    exportColumns.map((c) => {
      if (c.key === "run_number" && r.record_type === "chip_section") return "";
      if (c.key === "azimuth" && r.record_type === "chip_section") return "";
      if (c.key === "to_depth") return escape(getTotalDepth(r, holeType) ?? r.to_depth);
      return escape(c.display ? c.display(r) : r[c.key]);
    }).join(",")
  );
  const csv = [header, ...rows].join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  await deliverExport(blob, filename);
}

export async function exportRunsToPDF(runs, filename = "drill_runs.pdf", holeType = null) {
  if (!runs || runs.length === 0) {
    alert("No drill runs to export.");
    return;
  }

  const { jsPDF } = await import("jspdf");

  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  const companyName = safeLocalStorage.getItem("drilllog_company_name") || "";
  const companyLogo = safeLocalStorage.getItem("drilllog_company_logo") || "";
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 28;
  let y = margin;

  // Title
  if (companyLogo) {
    try {
      let logoData = companyLogo;
      if (!companyLogo.startsWith("data:image/")) {
        const response = await fetch(companyLogo);
        const blob = await response.blob();
        logoData = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      }
      const match = logoData.match(/^data:image\/(\w+)/);
      const format = ((match?.[1] || "png").toUpperCase() === "JPG") ? "JPEG" : (match?.[1] || "png").toUpperCase();
      doc.addImage(logoData, format, pageWidth - margin - 90, y - 4, 90, 28, undefined, "FAST");
    } catch {}
  }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(companyName ? `${companyName} — Drill Run Sheet` : "Drill Run Sheet", margin, y + 6);
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.text(pdfText(`Exported: ${new Date().toLocaleString()}`), margin, y + 20);
  doc.text(pdfText(`Total runs: ${runs.length}`), margin + 200, y + 20);
  y += 44;

  // Table headers
  const columns = [
    { key: "record_type", label: "Type", w: 42 },
    { key: "hole_id", label: "Hole", w: 48 },
    { key: "run_number", label: "Run", w: 28 },
    { key: "date", label: "Date", w: 48 },
    { key: "start_time", label: "Start", w: 40 },
    { key: "end_time", label: "End", w: 40 },
    { key: "from_depth", label: "From", w: 38 },
    { key: "to_depth", label: "To", w: 38 },
    { key: "recovered", label: "Recov", w: 42 },
    { key: "gain_loss", label: "G/L", w: 38 },
    { key: "viscosity_check", label: "Vis", w: 40 },
    { key: "flow_rate", label: "Flow", w: 42 },
    { key: "driller", label: "Driller", w: 58 },
    { key: "lithology", label: "Lithology", w: 85 },
    { key: "remarks", label: "Remarks", w: 80 },
    { key: "azimuth", label: "Azimuth", w: 45 },
  ];

  const exportColumns = holeType === "Conventional"
    ? columns.filter((c) => c.key !== "viscosity_check")
    : columns;

  const rowH = 28;
  doc.setFontSize(7.5);
  doc.setFont("helvetica", "bold");
  doc.setFillColor(15, 23, 42);
  doc.setTextColor(255, 255, 255);
  doc.rect(margin, y, pageWidth - margin * 2, rowH, "F");
  let x = margin + 4;
  exportColumns.forEach((c) => {
    doc.text(c.label, x, y + 11);
    x += c.w;
  });
  y += rowH;

  // Rows
  doc.setFont("helvetica", "normal");
  doc.setTextColor(30, 41, 59);
  let runCounter = 0;
  runs.forEach((r, i) => {
    if (y > doc.internal.pageSize.getHeight() - margin) {
      doc.addPage();
      y = margin;
    }
    const isChip = r.record_type === "chip_section";
    if (isChip) {
      doc.setFillColor(254, 243, 199);
      doc.rect(margin, y, pageWidth - margin * 2, rowH, "F");
    } else if (i % 2 === 1) {
      doc.setFillColor(241, 245, 249);
      doc.rect(margin, y, pageWidth - margin * 2, rowH, "F");
    }
    if (!isChip) runCounter += 1;
    // Type label
    let typeText = isChip ? "Chip" : "Run";
    x = margin + 4;
    doc.setFont("helvetica", "bold");
    doc.text(typeText, x, y + 11);
    doc.setFont("helvetica", "normal");
    x += columns[0].w;
    columns.slice(1).forEach((c) => {
      let val = r[c.key] ?? "";
      if (c.key === "run_number") val = isChip ? "" : runCounter;
      if (c.key === "azimuth" && isChip) val = "";
      const raw = String(val).length > 24 ? String(val).slice(0, 22) + "..." : String(val);
      doc.text(pdfText(raw), x, y + 11);
      x += c.w;
    });
    y += rowH;
  });

  // Summary footer — totals aligned under Run / To / G/L columns
  const xOf = (key) => {
    let xv = margin + 4;
    for (const c of columns) {
      if (c.key === key) return xv;
      xv += c.w;
    }
    return xv;
  };
  const totalDepth = Math.max(...runs.map((r) => getTotalDepth(r, holeType) || 0));
  const cumulativeGL = runs.reduce((s, r) => s + (Number(r.gain_loss) || 0), 0);
  const totalRuns = runs.filter((r) => r.record_type !== "chip_section").length;
  y += 8;
  if (y > doc.internal.pageSize.getHeight() - margin - 18) {
    doc.addPage();
    y = margin + 8;
  }
  doc.setDrawColor(203, 213, 225);
  doc.line(margin, y - 6, pageWidth - margin, y - 6);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(15, 23, 42);
  doc.text(String(totalRuns), xOf("run_number"), y);
  doc.text(String(totalDepth.toFixed(2)), xOf("to_depth"), y);
  doc.text(String(cumulativeGL.toFixed(2)), xOf("gain_loss"), y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(100, 116, 139);
  doc.text("Total Runs", xOf("run_number"), y + 9);
  doc.text("Total Depth (m)", xOf("to_depth"), y + 9);
  doc.text("Cumulative G/L (m)", xOf("gain_loss"), y + 9);

  // ---- Attached photos (final page) ----
  const num = (v) => (v == null ? "" : Number(v).toFixed(2));
  const loadImage = (url) =>
    new Promise((resolve, reject) => {
      fetch(url)
        .then((res) => res.blob())
        .then((blob) => {
          const reader = new FileReader();
          reader.onload = () => {
            const el = new Image();
            el.onload = () => resolve({ data: reader.result, w: el.naturalWidth, h: el.naturalHeight });
            el.onerror = reject;
            el.src = reader.result;
          };
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        })
        .catch(reject);
    });

  const photoRuns = runs.filter((r) => Array.isArray(r.photos) && r.photos.length > 0);
  if (photoRuns.length) {
    doc.addPage();
    let py = margin;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(15, 23, 42);
    doc.text("Attached Photos", margin, py + 6);
    py += 26;
    const maxW = pageWidth - margin * 2;
    const maxH = doc.internal.pageSize.getHeight() - margin * 2 - 40;
    doc.setTextColor(30, 41, 59);
    for (const r of photoRuns) {
      const range = pdfText(`${num(r.from_depth)}–${num(getTotalDepth(r, holeType) ?? r.to_depth)} m`);
      for (const url of r.photos) {
        try {
          const { data, w, h } = await loadImage(url);
          let iw = maxW;
          let ih = iw * (h / w);
          if (ih > maxH) { ih = maxH; iw = ih * (w / h); }
          if (py + ih + 22 > doc.internal.pageSize.getHeight() - margin) {
            doc.addPage();
            py = margin;
          }
          const ix = margin + (maxW - iw) / 2;
          const fmt =
            ((data.match(/^data:image\/(\w+)/) || [])[1] || "png").toLowerCase() === "jpeg" ? "JPEG" : "PNG";
          doc.addImage(data, fmt, ix, py, iw, ih);
          py += ih + 4;
          doc.setFont("helvetica", "bold");
          doc.setFontSize(10);
          doc.text(range, pageWidth / 2, py + 8, { align: "center" });
          py += 24;
        } catch (e) {
          /* skip unreadable image */
        }
      }
    }
  }

  const pdfBlob = doc.output("blob");
  await deliverExport(pdfBlob, filename);
}

